package fr.frux.feature.map

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import dev.zacsweers.metro.Inject
import fr.frux.core.ads.AdsController
import fr.frux.core.ads.RewardedResult
import fr.frux.core.common.error.AppError
import fr.frux.core.common.error.asAppError
import fr.frux.core.common.logging.Logger
import fr.frux.core.maps.Location
import fr.frux.core.maps.LocationPermissionStatus
import fr.frux.core.maps.LocationProvider
import fr.frux.core.maps.MapBounds
import fr.frux.core.maps.MapCameraPosition
import fr.frux.core.maps.toMapBounds
import fr.frux.feature.map.regime.MapRegimeController
import fr.frux.feature.map.site.MapArea
import fr.frux.shared.domain.favorites.FavoritesStore
import fr.frux.shared.domain.model.ClusterPyramid
import fr.frux.shared.domain.model.HarvestWindow
import fr.frux.shared.domain.model.MapFilter
import fr.frux.shared.domain.model.Place
import fr.frux.shared.domain.model.PlaceType
import fr.frux.shared.domain.model.SeasonalAround
import fr.frux.shared.domain.model.Species
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.model.currentHarvestMonth
import fr.frux.shared.domain.model.distanceMetersFrom
import fr.frux.shared.domain.model.needsCheck
import fr.frux.shared.domain.model.seasonalAround
import fr.frux.shared.domain.repository.ClusterPyramidRepository
import fr.frux.shared.domain.repository.PlaceRepository
import fr.frux.shared.domain.repository.SpeciesRepository
import fr.frux.shared.domain.repository.TreeRepository
import fr.frux.shared.domain.session.SessionState
import fr.frux.shared.domain.session.SessionStore
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.FlowPreview
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.emptyFlow
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.time.Instant
import kotlin.time.TimeMark
import kotlin.time.TimeSource

/** Camera de repli, assumee et etiquetee : ce n'est pas la position de l'utilisateur. */
val DEFAULT_CAMERA = MapCameraPosition(Location(48.8584, 2.2945), zoom = 13f)

/** Repos de geste : au-dela, l'utilisateur a fini de deplacer la carte (#125). */
internal const val CAMERA_DEBOUNCE_MS = 400L

internal const val DEFAULT_RADIUS_KM = 5.0

/**
 * Taille du lot d'arbres demande par la carte.
 *
 * Valait 20 par accident (defaut de `TreeApi`, que le repository ne surchargeait pas) : invisible
 * tant que la base tenait quelques centaines d'arbres de seed, intenable avec les 13 000 arbres
 * OpenStreetMap, ou Paris affichait 20 marqueurs sur 1 529 (#155).
 *
 * 500 venait d'une mesure d'aout 2026 : ~530 octets par arbre sur le reseau, et une surcouche
 * Compose qui reprojetait tous les marqueurs a chaque frame (tenait 1 000 sans depasser 32 ms).
 * Les deux couts ont change depuis : les reponses sont gzippees (#520, 10 Ko pour 500 arbres), la
 * projection SQL est resserree (#536), et les marqueurs vivent dans une source GeoJSON MapLibre
 * avec clustering natif (ADR 0007). Releve a 1 000 (#621), la borne serveur (`MAX_PAGE_SIZE`) :
 * a un zoom large, 500 ne couvraient qu'un disque autour du centre et laissaient le reste de
 * l'ecran vide. Au-dela, c'est le sujet des agregats serveur (#622), pas d'un lot plus gros.
 */
internal const val MAP_TREE_LIMIT = 1000

/**
 * Périmètre que « Voir tout » transmet à la liste des arbres (#229) — porté en primitives pour
 * voyager dans une route sérialisable sans exposer [Location] hors de la feature.
 */
data class SeeAllContext(
    val latitude: Double,
    val longitude: Double,
    val radiusKm: Double,
    /**
     * Position reelle de l'utilisateur au moment du « Voir tout », si elle est connue (#260).
     * Distincte du centre : celui-ci suit la camera, qui a pu etre trainee ailleurs. Sans elle,
     * la liste mesurerait ses distances depuis un point qui n'est pas l'utilisateur.
     */
    val userLatitude: Double? = null,
    val userLongitude: Double? = null,
)

/**
 * Plancher du rayon interroge. Un zoom a l'echelle de la rue donnerait un rayon quasi nul, que le
 * service rejette (`radiusKm <= 0` → 400).
 */
internal const val MIN_QUERY_RADIUS_KM = 0.5

/** Plafond du rayon interroge : borne du service (`TreeService.MAX_RADIUS_KM`). */
internal const val MAX_QUERY_RADIUS_KM = 500.0

/**
 * Taille du lot demande par « Voir les plus proches » : le serveur trie deja par distance
 * croissante, donc interroger au rayon plafond avec un petit lot suffit a obtenir les N arbres
 * correspondants les plus proches, sans nouveau chemin backend. Bien plus petit que
 * [MAP_TREE_LIMIT] : a l'echelle du pays, cadrer 500 marqueurs n'aiderait personne.
 */
internal const val NEAREST_MATCHES_LIMIT = 50

/**
 * ViewModel de l'ecran Explorer.
 *
 * Point cle : [MapState.queryCenter] (autour de quoi on interroge le backend) et
 * [MapState.userLocation] (position reelle) sont deux champs distincts. Les confondre revenait a
 * fabriquer une position utilisateur a Paris (#50).
 */
@Inject
class MapViewModel(
    private val treeRepository: TreeRepository,
    private val speciesRepository: SpeciesRepository,
    private val placeRepository: PlaceRepository,
    private val locationProvider: LocationProvider,
    private val viewStore: MapViewStore,
    private val favoritesStore: FavoritesStore,
    private val sessionStore: SessionStore,
    private val neverSeenInviteStore: NeverSeenInviteStore,
    /**
     * Masquage ou démasquage d'un compte (#515, ADR 0029) : la surface persistante (ADR 0009)
     * garde son lot en mémoire, chaque émission le marque périmé pour que [onMapBecameVisible]
     * recharge au retour. Défaut vide pour les tests qui n'exercent pas ce chemin.
     */
    private val contentVisibilityChanges: Flow<Unit> = emptyFlow(),
    /** Vidéo récompensée (#531), donné par `FruxCompositionRoot`. `null` dans les tests qui ne l'exercent pas. */
    private val adsController: AdsController? = null,
    private val areaRequested: (MapArea) -> Unit = {},
    /** Pyramide de regroupements (#622). `null` dans les tests qui ne l'exercent pas. */
    clusterPyramidRepository: ClusterPyramidRepository? = null,
    embeddedPyramid: EmbeddedPyramidSource = EmbeddedPyramidSource { readEmbeddedClusterPyramid() },
    pyramidOn: Boolean = PYRAMID_PREVIEW,
) : ViewModel() {
    private val _state = MutableStateFlow(MapState(queryCenter = DEFAULT_CAMERA.target))
    val state: StateFlow<MapState> = _state.asStateFlow()

    /** Collecte dans [viewModelScope] et non [userJob] : donnee publique, elle survit a [reset]. */
    private val regime = MapRegimeController(clusterPyramidRepository, embeddedPyramid::read, viewModelScope, pyramidOn)

    /** Pyramide de regroupements chargee, voir [MapRegimeController]. */
    val clusterPyramid: StateFlow<ClusterPyramid?> = regime.pyramid

    private val _events = Channel<MapEvent>(Channel.BUFFERED)
    val events: Flow<MapEvent> = _events.receiveAsFlow()

    /**
     * Parent de toutes les coroutines qui touchent aux donnees de l'utilisateur courant.
     *
     * Enfant de [viewModelScope] (le VM garde donc la main dessus), mais annulable seul :
     * [reset] le coupe et le remplace, ce qui neutralise d'un coup tout ce qui est en vol —
     * chargement, localisation, cadrage, debounces. Un `SupervisorJob` plutot qu'une liste de
     * jobs a maintenir : la liste, on oublie forcement d'y inscrire le prochain `launch`, et
     * c'est precisement l'oubli qui a laisse `refreshLocation()` survivre a la purge.
     */
    private var userJob = SupervisorJob(viewModelScope.coroutineContext[Job])

    /** Lance une coroutine rattachee a l'utilisateur courant, donc annulee par [reset]. */
    private fun launchForUser(block: suspend CoroutineScope.() -> Unit) = viewModelScope.launch(userJob, block = block)

    private var nearbyJob: kotlinx.coroutines.Job? = null

    /** Requete a part du plus proche sur zone vide (#502), hors de la classe (ratchet LargeClass). */
    private val nearestDistance = NearestDistanceLoader(treeRepository, ::launchForUser, _state)

    /** Fermeture persistante de la carte de zone vide, même horloge que l'invitation. */
    private val zoneMemory = EmptyAreaDismissController(viewStore, ::launchForUser, _state) { inviteNow() }

    /** Pastille de première session en zone peuplée, voir [PopulatedAreaHintController]. */
    private val populatedHint = PopulatedAreaHintController(viewStore, ::launchForUser, _state)

    /** Horloge de peremption du lot. Substituable en test (TestTimeSource), sinon monotone. */
    internal var timeSource: TimeSource = TimeSource.Monotonic

    /** Date du dernier lot recu avec succes. Null tant qu'aucun chargement n'a abouti. */
    private var treesLoadedAt: TimeMark? = null

    private val visibilityTracker = // Visibilite reelle de la carte (#515), voir MapVisibilityTracker.
        MapVisibilityTracker(viewModelScope, contentVisibilityChanges, { treesLoadedAt = null }, ::onMapBecameVisible)

    /** Rayon et rectangle du lot affiche, voir [LoadedView]. Reference des seuils d'armement. */
    private var loadedView = LoadedView.initial()

    /** Dernier lot non vide, son filtre et sa troncature, voir [LoadedBatchMemory]. */
    private val loadedBatch = LoadedBatchMemory()

    /** Confirmation d'ajout et feuille du premier arbre, voir [FirstContributionController]. */
    private val contributionAnnouncer = FirstContributionController(_state)

    /** Derniere vue rapportee par la camera : son centre et le rayon qu'elle couvre, en metres. */
    private val cameraView = MutableStateFlow<CameraView?>(null)

    /**
     * Le dernier lot recu avec succes vient-il d'un cache local perime (#62). Sert uniquement a
     * armer [MapState.showingStaleCache] si le chargement reseau qui suit echoue : tant que le
     * reseau n'a pas parle, rien ne dit a l'utilisateur que ses donnees sont perimees.
     */
    private var displayingStaleCache: Boolean = false

    /** Position, permissions et chaine de repli (#186), voir [LocationController]. */
    private val locationController =
        LocationController(
            locationProvider = locationProvider,
            launchForUser = ::launchForUser,
            state = _state,
            displayedTrees = { state.value.displayedTrees() },
            lastLoadedTrees = { loadedBatch.trees },
            lastLoadedFilter = { loadedBatch.filter },
            armFraming = { framesResults = true },
            frameVisibleResults = ::frameVisibleResults,
            mapVisible = visibilityTracker.visible,
            loadNearbyTrees = { center -> loadNearbyTrees(center) },
        )

    /**
     * Horloge de la fenetre de silence de l'invitation (#396). Substituable en test, meme raison
     * que [timeSource] : un plafond de 6 h ne se verifie pas en temps reel.
     */
    internal var inviteNow: () -> Instant = { Clock.System.now() }

    /** Invitation a confirmer un arbre jamais vu (#396), voir [NeverSeenInviteController]. */
    private val neverSeenInviteController =
        NeverSeenInviteController(
            treeRepository = treeRepository,
            store = neverSeenInviteStore,
            state = _state,
            now = { inviteNow() },
        )

    /** Saisie de recherche, debounce et suggestions (#186), voir [SpeciesSearchController]. */
    private val speciesSearchController =
        SpeciesSearchController(
            launchForUser = ::launchForUser,
            state = _state,
            armFraming = { framesResults = true },
            frameVisibleResults = ::frameVisibleResults,
            searchPlaces = placeRepository::searchPlaces,
        )

    /**
     * La camera cadre d'elle-meme ce qu'il y a a voir — les elements affiches et, si elle est
     * connue, la position de l'utilisateur. C'est la vue par defaut.
     *
     * Un geste la remet a l'utilisateur : tant qu'il tient la carte, rien ne la lui reprend. La
     * prochaine action qui renouvelle les elements (filtre, recherche, position, retry) rend le
     * cadrage automatique — le nouveau lot n'a aucune raison de tenir dans le cadre choisi pour
     * l'ancien.
     */
    private var framesResults = true

    /**
     * Dernier cadre demande a la camera. Ne pas redemander le meme evite les mouvements a vide :
     * cadrer d'entree puis rafraichir aboutit souvent au meme cadre. Un geste l'oublie — la camera
     * n'est alors plus la ou on l'avait laissee, et y revenir redevient un vrai deplacement.
     */
    private var lastFramedBounds: MapBounds? = null

    init {
        Logger.d(TAG, "MapViewModel initialized")
        // Aucun chargement ici : on ne sait pas encore quoi regarder. Interroger autour du repli
        // parisien affichait, pendant une seconde, des arbres de Paris comme s'ils etaient autour
        // de l'utilisateur — et jetait le lot des que sa position arrivait (#161). C'est l'hote
        // qui donne la vue de depart, une fois la vue memorisee relue.
        loadSpecies()
        observeCameraMoves()
        speciesSearchController.start()
        regime.start()
        viewModelScope.launch {
            favoritesStore.favoriteIds.collect { ids ->
                _state.update { it.copy(favoriteIds = ids) }
            }
        }
        observeUserLocationForInvite()
    }

    /**
     * Seul declencheur de l'invitation « personne n'est encore passe » (#396) : chaque position
     * fraiche obtenue, qu'elle vienne d'un tap sur « me localiser », de l'octroi de la permission
     * ou du suivi au premier plan. Branche ici plutot que dans [LocationController] pour que le
     * controleur de position ignore tout de cette fonction. Le pas minimal entre deux evaluations
     * vit dans [NeverSeenInviteController].
     */
    private fun observeUserLocationForInvite() {
        launchForUser {
            _state
                .map { it.userLocation }
                .filterNotNull()
                .distinctUntilChanged()
                .collect { neverSeenInviteController.onUserLocation(it) }
        }
    }

    /** L'invitation est suivie : l'ecran ouvre la fiche, le controle flottant disparait. */
    fun onNeverSeenInviteAccepted() = neverSeenInviteController.onAccepted()

    /** L'invitation est fermee : plus de sollicitation pour cette session. */
    fun onNeverSeenInviteDismissed() = neverSeenInviteController.onDismissed()

    /** La vue de depart a-t-elle deja ete donnee. La premiere est la seule qui compte. */
    private var initialViewApplied = false

    /**
     * La vue de depart venait-elle du store. Si oui, la permission de localisation ne recentre
     * plus : la camera appartient a l'utilisateur, et une vue memorisee est un choix qu'il a fait.
     */
    private var viewWasRestored = false

    /**
     * La persistance de la vue a-t-elle ete armee par un geste reel de l'utilisateur, cette
     * session. Posee a vrai dans [onCameraMoved], que l'hote n'appelle que pour les deplacements
     * de source GESTURE (filtre pose cote `MapHost`) — jamais pour un repli programmatique.
     *
     * Ce garde a d'abord ete une comparaison de positions (la derniere que le ViewModel s'etait
     * donnee lui-meme). Elle ne tenait pas en verification runtime : la camera rapportee par
     * MapLibre apres `contentPadding` et clamp de zoom ne correspond jamais exactement a la
     * position demandee, meme sans le moindre geste — le repli du tout premier lancement passait
     * donc le garde et s'ecrivait quand meme (#161). Une comparaison de flottants, exacte ou a
     * epsilon, ne peut pas porter cette decision ; savoir si un geste a eu lieu le peut.
     *
     * Un seul geste suffit a armer la persistance pour le reste de la session : un recadrage
     * programmatique ulterieur (apres une recherche, par exemple) merite d'etre memorise autant
     * qu'un pan, une fois que l'utilisateur a pris la main sur la carte.
     *
     * Choix assume : un utilisateur qui ne touche jamais la carte (uniquement « me localiser » ou
     * la recherche) ne verra donc aucune vue memorisee a la session suivante. C'est le cout de
     * l'armement par geste, prefere a une comparaison de positions qui ne tenait pas (voir plus
     * haut) — pas un oubli.
     */
    private var persistenceArmed = false

    /**
     * Vue de depart de la carte, donnee par l'hote une fois le store relu : la vue memorisee si
     * elle existe, le repli sinon.
     *
     * **L'hote doit l'appeler dans tous les cas**, echec de lecture compris — sans cet appel,
     * aucun arbre n'est jamais charge.
     */
    fun onInitialView(
        position: MapCameraPosition,
        restored: Boolean = false,
    ) {
        if (initialViewApplied) return
        initialViewApplied = true
        viewWasRestored = restored
        // Une vue memorisee est un cadre choisi par l'utilisateur : le lot charge autour ne doit
        // pas le lui reprendre, pas plus que la geolocalisation ne le fait deja (cf.
        // onPermissionResult). Sans ce garde, la camera s'ouvre bien sur la vue memorisee, puis
        // saute sur les bornes du premier lot recu des que la reponse arrive (#161).
        if (restored) framesResults = false
        loadNearbyTrees(position.target)
    }

    /**
     * Memorise la vue courante. Silencieux en cas d'echec : voir [MapViewStore].
     *
     * Ignore tant qu'aucun geste reel n'a arme la persistance — voir [persistenceArmed].
     */
    fun saveView(position: MapCameraPosition) {
        if (!persistenceArmed) return
        launchForUser { viewStore.save(position) }
    }

    /** Vue memorisee, ou null s'il n'y en a pas — ou si elle est illisible. */
    suspend fun restoredView(): MapCameraPosition? = viewStore.load()

    /**
     * Reaction au resultat de la demande de permission, transmise par l'UI. Delegue a
     * [locationController] : voir son KDoc pour la logique de deduplication et de repli.
     */
    fun onPermissionResult(status: LocationPermissionStatus) {
        locationController.onPermissionResult(status, viewWasRestored)
    }

    /**
     * Clic sur « ma position » alors que la permission manque : l'accord qui suivra doit
     * localiser, meme sur une vue restauree. Voir [LocationController.onLocateRequestedPendingPermission].
     */
    fun onLocateRequestedPendingPermission() = locationController.onLocateRequestedPendingPermission()

    /** Voir [LocationController.onLocationSettledByOnboarding]. */
    fun onLocationSettledByOnboarding() = locationController.onLocationSettledByOnboarding()

    /** Voir [LocationController.onLocateTapped]. */
    fun onLocateTapped() = locationController.onLocateTapped()

    /** Suit la personne tant que l'appelant reste collecte, voir [LocationController.followUserLocation]. */
    suspend fun followUserLocation() = locationController.followUserLocation()

    /**
     * Demande une position et recentre la carte si elle est obtenue. Delegue a
     * [locationController] : voir son KDoc.
     */
    fun refreshLocation() = locationController.refreshLocation()

    /**
     * Nouvelle vue rapportee par la couche de rendu, a chaque frame de geste. Ne requete rien :
     * [observeCameraMoves] decide seulement si la pastille « Rechercher dans cette zone » a lieu
     * d'etre proposee.
     *
     * L'hote n'appelle cette methode que pour les deplacements de source GESTURE : c'est donc
     * aussi le seul signal fiable qu'un utilisateur a reellement pris la main sur la carte, d'ou
     * l'armement de [persistenceArmed] ici.
     */
    fun onCameraMoved(
        center: Location,
        visibleRadiusMeters: Double,
        visibleBounds: MapBounds? = null,
    ) {
        cameraView.value = CameraView(center, visibleRadiusMeters, visibleBounds)
        persistenceArmed = true
        // La camera n'est plus ou on l'avait posee : redemander le meme cadre redevient utile.
        lastFramedBounds = null
    }

    /**
     * Arme la pastille quand la vue s'est assez ecartee du lot charge.
     *
     * Le rechargement automatique d'autrefois (#125) est retire : il etait invisible — rien ne
     * disait que la vue etait perimee, rien ne disait qu'elle avait ete rafraichie — et il
     * requetait sous les doigts de l'utilisateur. C'est desormais son geste qui decide.
     *
     * Le `debounce` reste, pour la raison inverse de l'originale : empecher la pastille
     * d'apparaitre et de disparaitre pendant le geste.
     *
     * L'ecart se mesure depuis [MapState.queryCenter] (le centre effectivement interroge), pas
     * depuis la position precedente de la camera : sinon une suite de petits pans eloignerait
     * indefiniment la carte du lot charge sans jamais armer la pastille.
     */
    @OptIn(FlowPreview::class)
    private fun observeCameraMoves() {
        launchForUser {
            cameraView
                .filterNotNull()
                .debounce(CAMERA_DEBOUNCE_MS)
                .collect { view ->
                    if (shouldArmAreaSearch(view, state.value.queryCenter, loadedView, loadedBatch.truncated)) {
                        Logger.d(TAG, "Vue ecartee du lot charge, pastille armee")
                        _state.update { it.copy(canSearchArea = true) }
                    }
                }
        }
    }

    /**
     * Recherche ce que la camera montre, a son centre et a son rayon.
     *
     * `framesResults = false` est le coeur du geste : la camera reste exactement ou l'utilisateur
     * l'a posee. Recadrer sur le lot recu annulerait ce qu'il vient de faire.
     */
    fun searchThisArea() {
        val view = cameraView.value ?: return
        _state.update { it.copy(canSearchArea = false, isSearchingArea = true) }
        framesResults = false
        loadNearbyTrees(view.center, radiusKm = view.radiusMeters / 1_000, bounds = view.bounds)
    }

    /**
     * Ferme la carte compacte de zone vide (#502). Silence persistant de sept jours, levé plus tôt
     * par un centre à plus de deux kilomètres (spec onboarding de contribution §2).
     */
    fun dismissEmptyAreaCard() = zoneMemory.dismiss(state.value.queryCenter)

    fun dismissPopulatedHint() = populatedHint.dismiss()

    /**
     * « Voir les plus proches » : la zone est vide sous le filtre actif, elargir au rayon plafond
     * avec un petit lot ([NEAREST_MATCHES_LIMIT]). Le serveur triant par distance, le lot recu est
     * exactement les arbres correspondants les plus proches, et la camera se recadre dessus.
     */
    fun showNearestMatches() {
        framesResults = true
        loadNearbyTrees(
            state.value.queryCenter,
            radiusKm = MAX_QUERY_RADIUS_KM,
            limit = NEAREST_MATCHES_LIMIT,
            // Une selection d'espece est un filtre client (voir MapFilter.rollupId) : sans la
            // transmettre ici, le serveur rendrait les N arbres les plus proches toutes especes
            // confondues, dont potentiellement aucun de l'espece cherchee.
            rollupId = state.value.selectedSpeciesRollupId,
        )
    }

    /**
     * Contexte que « Voir tout » transmet à la liste (#229) : ce que la carte montre — centre et
     * rayon de la caméra si elle a déjà rapporté une vue, sinon le dernier périmètre interrogé.
     * Même clamp que [loadNearbyTrees] : le service rejette un rayon hors bornes.
     */
    fun seeAllContext(): SeeAllContext =
        seeAllContextFrom(cameraView.value, state.value.queryCenter, loadedView.radiusMeters, state.value.userLocation)

    /**
     * Cadre ce que l'utilisateur regarde : les elements affiches, plus sa position si elle est
     * connue. Sans position, les elements seuls — jamais une position par defaut, qui ferait
     * croire a l'utilisateur qu'il est a Paris (#50).
     *
     * Ne fait rien s'il n'y a rien a montrer : mieux vaut laisser la camera ou elle est que la
     * renvoyer sur un repli.
     */
    private suspend fun frameVisibleResults() {
        if (!framesResults) return

        val current = state.value
        val points =
            current.displayedTrees().map { Location(it.latitude, it.longitude) } +
                listOfNotNull(current.userLocation)
        val bounds = points.toMapBounds() ?: return

        // Deja cadre, et rien n'a bouge depuis : ne pas relancer une animation pour rien.
        if (bounds == lastFramedBounds) return
        lastFramedBounds = bounds

        if (bounds.diagonalMeters < MIN_FRAMEABLE_SPAN_METERS) {
            _events.send(MapEvent.CenterOn(bounds.center))
        } else {
            _events.send(MapEvent.FrameOn(bounds))
        }
    }

    /** Charge le referentiel une seule fois : une indisponibilite ne doit pas casser la carte. */
    private fun loadSpecies() {
        viewModelScope.launch {
            speciesRepository.getSpecies().fold(
                onSuccess = { species ->
                    _state.update {
                        it.copy(
                            species = species,
                            speciesRollup = species.associate { s -> s.id to s.rollupId },
                            // La saisie peut avoir fini son debounce avant le referentiel : dans
                            // ce cas, publier les suggestions ici evite d'attendre une nouvelle touche.
                            speciesSuggestions =
                                if (it.selectedSpeciesRollupId == null) {
                                    matchingSpecies(it.searchQuery, species)
                                } else {
                                    emptyList()
                                },
                        )
                    }
                },
                onFailure = { Logger.e(TAG, "Referentiel indisponible, recherche degradee", it) },
            )
        }
    }

    fun dismissLocationNotice() {
        _state.update { it.copy(locationNotice = null) }
    }

    /**
     * Charge les arbres autour de [center]. Le chargement precedent est annule : [MapState.queryCenter]
     * est ecrit au debut et [MapState.nearbyTrees] a la fin, donc une requete plus ancienne qui repond
     * en dernier afficherait les arbres d'un centre perime sous le nouveau centre (#116).
     */
    fun loadNearbyTrees(
        center: Location,
        radiusKm: Double = DEFAULT_RADIUS_KM,
        limit: Int = MAP_TREE_LIMIT,
        rollupId: Long? = null,
        bounds: MapBounds? = null,
    ) {
        nearbyJob?.cancel()
        nearestDistance.cancel()
        val requestedFilter = state.value.filter.copy(rollupId = rollupId)
        val clampedRadius = radiusKm.coerceIn(MIN_QUERY_RADIUS_KM, MAX_QUERY_RADIUS_KM)
        nearbyJob =
            launchForUser {
                _state.update { it.loadingAround(center) }
                areaRequested(MapArea(center, clampedRadius, bounds))

                treeRepository
                    .getNearbyTrees(
                        lat = center.latitude,
                        lon = center.longitude,
                        radius = clampedRadius,
                        filter = requestedFilter,
                        limit = limit,
                        bounds = bounds,
                    ).collect { result ->
                        result.fold(
                            onSuccess = { nearby ->
                                val trees = nearby.trees
                                Logger.i(TAG, "Charge ${trees.size} arbres sur ${nearby.totalAvailable}")
                                treesLoadedAt = timeSource.markNow()
                                loadedView = LoadedView(clampedRadius * 1_000, bounds)
                                displayingStaleCache = nearby.isStale
                                // Ecart au total serveur mesure sur le lot brut (nearbyTrees), jamais
                                // sur visibleTrees : celui-ci est deja restreint cote client par le
                                // rollup d'espece, son rapport au total serveur serait faux.
                                val truncatedTotal = nearby.totalAvailable.takeIf { it > trees.size }
                                // Exact depuis que le total serveur voyage (#155) : l'ancienne
                                // heuristique `>= MAP_TREE_LIMIT` ratait un lot plafonne pile a la limite.
                                loadedBatch.truncated = truncatedTotal != null
                                loadedBatch.remember(trees, requestedFilter)
                                val emptyArea = emptyAreaFor(trees, requestedFilter, clampedRadius)
                                val emptyAreaSilenced = emptyArea != null && zoneMemory.isSilenced(center)
                                _state.update {
                                    it.withNearbyBatch(
                                        trees,
                                        requestedFilter,
                                        clampedRadius,
                                        truncatedTotal,
                                        emptyArea,
                                        emptyAreaSilenced,
                                    )
                                }
                                // Jamais sous une selection d'espece : filtre client, le bandeau
                                // filtre prime et la requete serait du reseau pour rien.
                                val noSpeciesSelected = state.value.selectedSpeciesRollupId == null
                                populatedHint.onTreesLoaded(trees, requestedFilter)
                                if (emptyArea == FilteredNoResults.IN_AREA && noSpeciesSelected) {
                                    nearestDistance.load(center)
                                }
                                frameVisibleResults()
                            },
                            onFailure = { error ->
                                Logger.e(TAG, "Echec du chargement des arbres", error)
                                _state.update { it.withNearbyFailure(error.asAppError(), displayingStaleCache) }
                            },
                        )
                    }
            }
    }

    /**
     * Recharge la vue courante a l'echelle du lot affiche ([loadedView] : rayon et rectangle), pour
     * les chemins qui la rafraichissent sous d'autres conditions (retry, retour sur l'ecran,
     * changement ou effacement de filtre). Sinon une recherche de zone a 20 km se ferait ramener a
     * [DEFAULT_RADIUS_KM] au premier filtre coche, et la pastille se rearmerait aussitot. Les
     * chemins qui ramenent deliberement au quartier (se localiser, viser un arbre cree) gardent
     * [DEFAULT_RADIUS_KM] : un rayon regional n'a pas de sens pour un point precis.
     * [frames] : cadrer ou non la camera sur le lot recu, voir [framesResults].
     */
    private fun reloadSameView(frames: Boolean) {
        framesResults = frames
        loadNearbyTrees(state.value.queryCenter, radiusKm = loadedView.radiusMeters / 1_000, bounds = loadedView.bounds)
    }

    fun retry() {
        _state.update { it.copy(canSearchArea = false) }
        reloadSameView(frames = true)
    }

    /**
     * L'ecran carte redevient l'ecran courant. Le ViewModel etant permanent, c'est le seul signal
     * de « retour » restant : recharge le lot s'il est perime ([TREES_STALE_AFTER]), sans toucher
     * a la camera — l'utilisateur revient sur la vue exacte qu'il avait laissee.
     *
     * Le garde sur [nearbyJob] couvre une fenetre que `isLoading` seul ne voit pas :
     * [loadNearbyTrees] lance une coroutine qui pose `isLoading = true` par un `_state.update`, pas
     * de facon synchrone, alors que `nearbyJob` lui-meme est affecte des le retour de la fonction.
     * Entre l'appel a `onInitialView` et la premiere execution de cette coroutine, `isLoading` vaut
     * encore faux et `treesLoadedAt` encore null — si `MapScreen` se composait dans cette fenetre,
     * son effet de montage appelait `onMapBecameVisible` et les deux gardes existants laissaient
     * passer un second chargement identique (#161, defaut releve en verification runtime).
     */
    fun onMapBecameVisible() {
        if (state.value.isLoading || nearbyJob?.isActive == true) return
        val loadedAt = treesLoadedAt
        if (loadedAt == null || loadedAt.elapsedNow() > TREES_STALE_AFTER) {
            Logger.d(TAG, "Lot perime au retour sur la carte, rechargement")
            reloadSameView(frames = false)
        }
    }

    fun onMapVisibilityChanged(visible: Boolean) = visibilityTracker.onVisibilityChanged(visible) // MapSurface (#515)

    /**
     * Plusieurs marqueurs indistinguables au toucher : ouvre la feuille de choix. Etat porte par
     * le ViewModel et non par l'ecran — les clics arrivent de la surface carte persistante, qui
     * survit a l'ecran (#124).
     */
    fun onMarkerGroupSelected(treeIds: List<Long>) =
        _state.update { it.copy(selectedTreeId = null, markerGroupIds = treeIds) }

    /** Referme la feuille de choix de groupe. */
    fun dismissMarkerGroup() = _state.update { it.copy(markerGroupIds = emptyList()) }

    /**
     * Un arbre vient d'être créé (#129 lot 2) : centrer la caméra dessus et recharger le lot
     * autour. Le cadrage automatique est désarmé, la caméra reste sur l'arbre désigné.
     * [NewTreeFocus.firstHereConfirmation] est lu AVANT le rechargement, qui effacera `emptyArea`.
     */
    fun focusOnNewTree(focus: NewTreeFocus) {
        val wasEmptyHere = state.value.emptyArea != null
        centerAndReload(focus.target, focus.zoom)
        populatedHint.onTreeAdded()
        contributionAnnouncer.announce(wasEmptyHere, focus)
    }

    internal fun centerAndReload(
        target: Location,
        zoom: Float? = null,
    ) {
        framesResults = false
        launchForUser { _events.send(MapEvent.CenterOn(target, zoom)) }
        loadNearbyTrees(target)
    }

    /**
     * Consomme le message de confirmation apres affichage (#129 lot 2). Porte dans l'etat du
     * ViewModel plutot que dans un `remember` d'ecran : ce dernier ne survit pas a la
     * recomposition de l'hote apres `onFocusConsumed()`, qui recree `fruxEntryProvider` et coupe
     * la coroutine suspendue dans `showSnackbar` en plein vol.
     */
    fun consumeConfirmation() = contributionAnnouncer.consumeConfirmation()

    /** Ferme la feuille du premier arbre (spec §4). Les ajouts suivants reviennent au snackbar. */
    fun consumeFirstContribution() = contributionAnnouncer.consume()

    fun announceTreeRemoved(
        treeId: Long,
        message: String,
    ) = contributionAnnouncer.announceRemoval(treeId, message)

    /**
     * Bascule le favori de l'arbre sélectionné. L'optimisme et le rollback vivent dans le
     * [FavoritesStore] — ici on ne gère que le refus (message) et l'anonyme (invite).
     */
    fun onFavoriteClick() {
        val treeId = _state.value.selectedTreeId ?: return
        if (sessionStore.state.value !is SessionState.Authenticated) {
            _state.update { it.copy(showLoginPrompt = true) }
            return
        }
        viewModelScope.launch {
            favoritesStore.toggle(treeId).onFailure { error ->
                _state.update { it.copy(favoriteError = error.asAppError()) }
            }
        }
    }

    /** Consomme le message d'erreur favori après affichage (snackbar). */
    fun consumeFavoriteError() = _state.update { it.copy(favoriteError = null) }

    /** Consomme l'invite de connexion après affichage (snackbar). */
    fun consumeLoginPrompt() = _state.update { it.copy(showLoginPrompt = false) }

    private val rewardedLauncher = RewardedAdLauncher(viewModelScope, adsController, _state)

    /**
     * Lien « Regarder une vidéo pour 24 h sans pub » sous la bannière ou l'annonce native (#531),
     * résultat dans l'état.
     */
    fun onRemoveAdsRequested() = rewardedLauncher.launch()

    /** Consomme le résultat de la vidéo après affichage (snackbar). */
    fun consumeRewardedResult() = _state.update { it.copy(rewardedResult = null) }

    /**
     * La saisie est exposee immediatement (le champ doit suivre la frappe), la recherche elle-meme
     * part dans [SpeciesSearchController]. Delegue au controleur, voir son KDoc.
     */
    fun onSearchQueryChange(query: String) {
        speciesSearchController.onQueryChange(query)
    }

    fun onFilterChange(filter: MapFilter) {
        // Un filtre purement local (masquer les arbres a verifier, #265) ne redemande rien : le
        // lot en place suffit, `visibleTrees` s'en charge. Le vider et relancer une requete pour
        // ce seul changement ferait clignoter la carte sans rien apprendre de neuf.
        if (filter.query == state.value.filter.query) {
            _state.update { it.copy(filter = filter, selectedTreeId = null) }
            return
        }
        _state.update {
            it.copy(
                filter = filter,
                // Le lot courant a ete obtenu avec l'ancien filtre. Le garder apres un echec
                // ferait mentir la puce active sans aucun message, puisque l'erreur plein ecran
                // n'est montree que lorsque le lot est vide.
                nearbyTrees = emptyList(),
                searchResults = emptyList(),
                placeResults = emptyList(),
                selectedTreeId = null,
                isLoading = true,
                canSearchArea = false,
                error = null,
                truncatedTotal = null,
            )
        }
        reloadSameView(frames = true)
    }

    /** Efface en une seule transition les filtres serveur et la selection locale d'espece. */
    fun clearFilters() {
        _state.update {
            it.copy(
                filter = MapFilter.None,
                selectedSpeciesRollupId = null,
                speciesSuggestions = emptyList(),
                searchQuery = "",
                searchResults = emptyList(),
                placeResults = emptyList(),
                nearbyTrees = emptyList(),
                selectedTreeId = null,
                isLoading = true,
                canSearchArea = false,
                error = null,
                truncatedTotal = null,
            )
        }
        speciesSearchController.clear()
        reloadSameView(frames = true)
    }

    /** Choisit une espece de suggestion, ou efface ce filtre local avec `null`. */
    fun onSpeciesSelected(species: Species?) {
        _state.update {
            val rollupId = species?.rollupId
            val selectedTreeStillVisible =
                it.selectedTreeId?.let { selectedId ->
                    it.nearbyTrees.any { tree ->
                        tree.id == selectedId &&
                            (rollupId == null || it.speciesRollup[tree.species.id] == rollupId)
                    }
                } ?: false
            val visibleAfterSelection = visibleUnderRollup(it.nearbyTrees, rollupId, it.speciesRollup)
            it.copy(
                selectedSpeciesRollupId = rollupId,
                speciesSuggestions = emptyList(),
                searchQuery = species?.commonNameFr ?: "",
                // Une selection est deja un filtre explicite : un resultat textuel perime ne doit
                // jamais masquer les varietes appartenant au meme rollup.
                searchResults = emptyList(),
                placeResults = emptyList(),
                selectedTreeId = it.selectedTreeId.takeIf { selectedTreeStillVisible },
                // Pas pendant un chargement : c'est son issue qui tranchera, sur le lot frais.
                // Jamais ANYWHERE ici : le lot en place peut venir d'un elargissement restreint a
                // une AUTRE espece, il ne prouve rien sur celle-ci. Seul un chargement au rayon
                // plafond pour cette selection peut conclure « rien nulle part ».
                filteredNoResults =
                    if (it.isLoading) {
                        it.filteredNoResults
                    } else {
                        selectionNoResults(visibleAfterSelection, it.filter, rollupId)
                    },
            )
        }
        // Ne pas republier le nom choisi dans le flux de saisie : cela recreerait, 300 ms plus
        // tard, des resultats textuels et des suggestions que la selection vient de fermer.
        if (species == null) speciesSearchController.clear()
        framesResults = true
        launchForUser { frameVisibleResults() }
    }

    /**
     * Selectionne un arbre. Seule une selection issue d'une liste recentre la camera :
     * recentrer sur un marqueur tape deplacerait la carte sous le doigt de l'utilisateur.
     *
     * Toujours selectionner, jamais basculer : c'est le comportement attendu d'un resultat de
     * recherche, qui designe un arbre precis et ne doit pas se refermer si c'est deja le bon.
     * Pour un element qui porte lui-meme l'etat selectionne, voir [toggleTree].
     */
    fun selectTree(
        treeId: Long,
        origin: SelectionOrigin = SelectionOrigin.MAP,
    ) {
        _state.update { it.copy(selectedTreeId = treeId) }

        if (origin == SelectionOrigin.LIST) {
            val tree = state.value.nearbyTrees.find { it.id == treeId } ?: return
            // Viser un arbre precis, c'est deja choisir son cadre : ne pas le rouvrir sur tout le
            // lot au prochain chargement.
            framesResults = false
            launchForUser {
                _events.send(MapEvent.CenterOn(Location(tree.latitude, tree.longitude)))
            }
        }
    }

    /**
     * Choisir un lieu (#394) : la camera y va, la recherche s'efface, aucun ecran ne s'ouvre
     * (ADR 0009). Le lot d'arbres autour suit le mouvement de camera comme apres n'importe quel
     * geste, et ne doit pas reprendre le cadrage : l'utilisateur a choisi ou regarder.
     */
    fun selectPlace(place: Place) {
        speciesSearchController.onQueryChange("")
        _state.update { it.copy(selectedTreeId = null, canSearchArea = false) }
        centerAndReload(place.location, zoom = place.type.targetZoom())
    }

    /**
     * Vue demandee par un lien recu a chaud (App Links) : deselectionne, recentre et recharge,
     * par le meme chemin que [selectPlace], sans toucher a la pile ni a la surface (ADR 0009).
     */
    fun showLinkedView(view: MapCameraPosition) {
        clearSelection()
        centerAndReload(view.target, view.zoom)
    }

    /**
     * Bascule la selection : re-toucher l'arbre deja selectionne le deselectionne (#127).
     *
     * Reserve aux elements qui affichent leur propre etat selectionne — le marqueur sur la carte
     * et la pastille « a proximite ». Ils designent le meme arbre a l'ecran, donc se retoucher
     * doit produire le meme effet sur les deux.
     */
    fun toggleTree(
        treeId: Long,
        origin: SelectionOrigin = SelectionOrigin.MAP,
    ) {
        if (state.value.selectedTreeId == treeId) {
            clearSelection()
        } else {
            selectTree(treeId, origin)
        }
    }

    /** Referme la fiche d'arbre sans en ouvrir une autre (#127). */
    fun clearSelection() {
        _state.update { it.copy(selectedTreeId = null) }
    }

    /**
     * Purge tout ce qui appartient a l'utilisateur qui part (#48).
     *
     * La surface carte est persistante et hors pile de navigation (ADR 0009) : rien ne la demonte
     * a la deconnexion, donc personne d'autre n'efface ce qu'elle porte. Ce qui reste a l'ecran
     * apres un logout — arbres autour de chez soi, recherche, selection, position — serait visible
     * du prochain utilisateur du meme appareil.
     *
     * Le referentiel d'especes est le seul rescape : contenu public et non nominatif, le
     * recharger serait du travail pur perte.
     */
    fun reset() {
        // 1. Couper tout ce qui est en vol. Annuler le seul nearbyJob ne suffisait pas :
        // localisation, cadrage et debounces ecrivent aussi dans l'etat, depuis leurs propres
        // coroutines, et reprenaient apres la purge.
        userJob.cancel()
        userJob = SupervisorJob(viewModelScope.coroutineContext[Job])
        nearbyJob = null

        // 2. Vider le canal d'evenements. Son collecteur vit ailleurs (MapSurface) : un CenterOn
        // deja emis serait consomme apres coup et recadrerait la carte sur le partant.
        do {
            val drained = _events.tryReceive()
        } while (drained.isSuccess)

        treesLoadedAt = null
        loadedBatch.reset()
        lastFramedBounds = null
        framesResults = true
        cameraView.value = null
        loadedView = LoadedView.initial()
        displayingStaleCache = false
        speciesSearchController.clear()
        initialViewApplied = false
        viewWasRestored = false
        // Desarme la persistance : MapHostState.reset() repose la camera sur DEFAULT_CAMERA juste
        // apres cet appel, un mouvement programmatique que le collecteur permanent de MapSurface ne
        // doit pas prendre pour un choix du nouvel utilisateur (voir le KDoc de persistenceArmed).
        // Le premier geste reel de ce nouvel utilisateur rearme saveView de lui-meme, sans qu'il
        // faille redonner de vue initiale.
        persistenceArmed = false
        // La camera est deja purgee par MapHostState (ADR 0009, #48) ; sans cette ligne, la vue
        // du partant survivrait sur le disque et serait restauree pour le suivant.
        viewModelScope.launch { viewStore.clear() }
        zoneMemory.forget()
        populatedHint.forget()
        _state.value =
            MapState(
                queryCenter = DEFAULT_CAMERA.target,
                species = state.value.species,
                speciesRollup = state.value.speciesRollup,
            )

        // Le statut de permission est repris a zero : c'est un etat de l'appareil, mais le
        // ViewModel ne s'en sert que pour dedupliquer un GRANTED rejoue (#124). Le garder
        // rendrait le GRANTED du nouvel utilisateur « deja vu », donc muet — il devrait taper
        // « me localiser » a la main la ou un demarrage a froid l'aurait localise seul.
        locationController.reset()

        // Le refus d'invitation appartenait au partant. Le plafond persistant, lui, reste en place
        // a dessein : il protege l'appareil, pas le compte.
        neverSeenInviteController.reset()

        // Les collecteurs permanents etaient enfants de userJob : les relancer, sinon la carte du
        // nouvel utilisateur resterait sourde a la recherche et aux deplacements de camera. C'est
        // aussi ce qui neutralise un debounce arme avant la purge.
        observeCameraMoves()
        speciesSearchController.start()
        observeUserLocationForInvite()
    }

    companion object {
        private const val TAG = "MapViewModel"
    }
}

/** Origine d'une selection : conditionne le recentrage de la camera. */
enum class SelectionOrigin { MAP, LIST }

/** Zoom cible d'un lieu choisi (#394) : une commune de loin, une adresse de pres. */
internal fun PlaceType.targetZoom(): Float =
    when (this) {
        PlaceType.MUNICIPALITY -> 12f
        PlaceType.LOCALITY -> 14f
        PlaceType.STREET, PlaceType.POI -> 15f
        PlaceType.ADDRESS -> 16f
    }

/** Evenements one-shot vers l'UI. Ne jamais les stocker en etat : ils seraient rejoues. */
sealed interface MapEvent {
    /**
     * Recentrer la camera. [zoom] `null` = zoom de selection d'arbre ([SELECTED_TREE_ZOOM]) ;
     * un lieu choisi (#394) impose le sien, une commune ne se regarde pas a l'echelle d'un arbre.
     */
    data class CenterOn(
        val target: Location,
        val zoom: Float? = null,
    ) : MapEvent

    /** Cadrer tout ce qu'il y a a voir. Le zoom revient a la couche de rendu, seule a connaitre la vue. */
    data class FrameOn(
        val bounds: MapBounds,
    ) : MapEvent
}

/** Debut d'un chargement autour de [center] : les constats du lot precedent ne valent plus. */
private fun MapState.loadingAround(center: Location): MapState =
    copy(
        queryCenter = center,
        isLoading = true,
        error = null,
        filteredNoResults = null,
        emptyArea = null,
        nearestDistanceMeters = null,
    )

/**
 * Un chargement sous filtre serveur actif n'a rien donne. [IN_AREA] : la zone interrogee est vide,
 * proposer « Voir les plus proches ». [ANYWHERE] : meme au rayon plafond il n'y a rien, constat
 * sans action possible.
 */
enum class FilteredNoResults {
    IN_AREA,
    ANYWHERE,
}

/** Ce qu'il faut signaler a l'utilisateur au sujet de sa localisation. */
enum class LocationNotice {
    PERMISSION_DENIED,
    PERMISSION_PERMANENTLY_DENIED,
    UNAVAILABLE,
}

@OptIn(ExperimentalTime::class)
data class MapState(
    /** Autour de quoi le backend est interroge. Toujours renseigne. */
    val queryCenter: Location,
    /** Position reelle de l'utilisateur, ou null tant qu'aucun fix n'a ete obtenu. */
    val userLocation: Location? = null,
    val locationNotice: LocationNotice? = null,
    /** Une demande de position est en cours : le bouton « me localiser » doit le montrer. */
    val isLocating: Boolean = false,
    /**
     * Nombre de demandes de position terminees, aboutie ou non. Un observateur qui attend la fin
     * d'une mesure compare a la valeur relevee au depart : [isLocating] seul ne suffit pas, un
     * StateFlow conflate son aller-retour quand le fix est immediat (#672).
     */
    val locateSequence: Int = 0,
    /**
     * La position a été tranchée dans l'accueil (#502, refus ou « Plus tard ») : la carte ne la
     * redemande pas au lancement et tait le bandeau de refus jusqu'au prochain geste sur
     * « ma position ».
     */
    val locationPromptSuppressed: Boolean = false,
    /**
     * La vue s'est assez ecartee du lot charge pour qu'une nouvelle recherche ait un sens : la
     * pastille « Rechercher dans cette zone » est proposee.
     */
    val canSearchArea: Boolean = false,
    /**
     * Une recherche de zone est en vol. Distinct de [isLoading], qui couvre tous les chargements
     * et pilote la surcouche d'attente plein ecran : ici la carte reste utilisable, seule la
     * pastille montre l'attente.
     */
    val isSearchingArea: Boolean = false,
    val nearbyTrees: List<Tree> = emptyList(),
    /**
     * Total serveur disponible dans le rayon interroge quand il depasse [nearbyTrees].size, sinon
     * `null` (#155). Mesure sur le lot brut, pas [visibleTrees] : voir le commentaire dans
     * [fr.frux.feature.map.MapViewModel.loadNearbyTrees].
     */
    val truncatedTotal: Int? = null,
    val isLoading: Boolean = false,
    val searchQuery: String = "",
    val searchResults: List<Tree> = emptyList(),
    /** Lieux geocodes pour la saisie courante (#394), vides sous [MIN_PLACE_QUERY_LENGTH] ou en echec. */
    val placeResults: List<Place> = emptyList(),
    val filter: MapFilter = MapFilter.None,
    /** Referentiel charge pour les suggestions et l'agregation des varietes. */
    val species: List<Species> = emptyList(),
    /** Index espece -> rollup, derive une fois au chargement du referentiel. */
    val speciesRollup: Map<Long, Long> = emptyMap(),
    val speciesSuggestions: List<Species> = emptyList(),
    val selectedSpeciesRollupId: Long? = null,
    val showFilters: Boolean = true,
    val selectedTreeId: Long? = null,
    /** Catégorie d'échec du chargement (#176) ; le libellé est résolu par l'écran. */
    val error: AppError? = null,
    /**
     * Le dernier chargement, filtre serveur actif, n'a rien donne : bandeau explicatif, avec
     * proposition d'elargissement quand elle a un sens (voir [FilteredNoResults]).
     */
    val filteredNoResults: FilteredNoResults? = null,
    /**
     * Le dernier chargement, sans aucun filtre, n'a rapporte aucun arbre (#478) : meme bandeau
     * que [filteredNoResults], avec un texte qui ne parle pas de filtre.
     */
    val emptyArea: FilteredNoResults? = null,
    /**
     * Distance, en metres, de l'arbre le plus proche quand la zone est vide (#502), obtenue par
     * une requete a part au rayon plafond et limite 1. Null tant qu'elle n'a pas repondu, en cas
     * d'echec, ou quand rien n'existe en France.
     */
    val nearestDistanceMeters: Double? = null,
    /** La carte compacte de zone vide est fermee pour cette zone (#502, silence persistant). */
    val emptyAreaSilenced: Boolean = false,
    /** Pastille de première session en zone peuplée à l'écran (lot 3, une seule exposition). */
    val populatedHint: Boolean = false,
    /**
     * Le lot affiche vient du cache local perime, et le rechargement reseau qui a suivi a echoue
     * (#62) : un bandeau discret le signale, sans bloquer l'ecran comme [error] le ferait — les
     * donnees restent utilisables, juste potentiellement datees.
     */
    val showingStaleCache: Boolean = false,
    /** Message de confirmation one-shot (#129 lot 2), ex. « Arbre ajouté » apres une creation. */
    val confirmationMessage: String? = null,
    /** Arbre créé que l'action « Voir » de la confirmation ouvre, nul sans action. */
    val confirmationTreeId: Long? = null,
    /** Feuille « Ton premier arbre est sur la carte », en attente d'affichage (spec §4). */
    val firstContribution: FirstContributionCelebration? = null,
    /** Arbres d'un groupe de marqueurs indistinguables, en attente de choix ([onMarkerGroupSelected]). */
    val markerGroupIds: List<Long> = emptyList(),
    /**
     * Mois courant, calculé une fois à la construction de l'état initial et conservé tel quel par
     * les `copy()` suivants : dérive le statut/la saison affichés pour les arbres sans statut
     * déclaré (cf. [fr.frux.feature.map.resolvedStatus]).
     */
    val currentMonth: Int = currentHarvestMonth(),
    /**
     * Horloge figée à la construction de l'état initial, substituable en test (#444) : mêmes
     * garanties que [currentMonth], utilisée pour périmer un statut de terrain trop ancien
     * (cf. [fr.frux.feature.map.resolvedStatus]).
     */
    val now: Instant = Clock.System.now(),
    /** Ids favoris, reflet du FavoritesStore (source unique, spec 2026-08-12). */
    val favoriteIds: Set<Long> = emptySet(),
    /** Échec one-shot quand le serveur refuse la bascule du favori ; libellé résolu par l'écran (#176). */
    val favoriteError: AppError? = null,
    /** One-shot : l'anonyme a tapé le cœur, proposer la connexion. */
    val showLoginPrompt: Boolean = false,
    /**
     * Invitation a confirmer un arbre jamais vu, quand on se trouve devant (#396). `null` la
     * plupart du temps : elle est plafonnee, cf. [NeverSeenInviteController].
     */
    val neverSeenInvite: NeverSeenInvite? = null,
    /** Résultat de la dernière vidéo récompensée (#531), à montrer une fois en snackbar. */
    val rewardedResult: RewardedResult? = null,
    /** Vidéo récompensée en cours de chargement ou de lecture : le lien reste là, désactivé (#531). */
    val rewardedLoading: Boolean = false,
) {
    val selectedTree: Tree?
        get() = visibleTrees.find { it.id == selectedTreeId }

    val isSelectedTreeFavorite: Boolean
        get() = selectedTreeId != null && selectedTreeId in favoriteIds

    /** Fenêtres de récolte par espèce, dérivées du référentiel chargé ([species]). */
    val speciesHarvests: Map<Long, List<HarvestWindow>>
        get() = species.associate { it.id to it.harvests }

    /**
     * Mise en avant « de saison » (#403, ADR 0020) : croise le referentiel avec [nearbyTrees], pas
     * [visibleTrees] — la feuille « Autour de toi » doit refleter tout ce qui est charge, pas ce
     * qu'un filtre d'espece a deja restreint.
     */
    val seasonalAround: SeasonalAround
        get() = seasonalAround(species, nearbyTrees, currentMonth)

    /** La carte compacte de zone vide (#502) se montre tant qu'elle n'est pas fermee pour cette zone. */
    val showsEmptyAreaCard: Boolean
        get() = emptyArea != null && !emptyAreaSilenced

    /**
     * Lot effectivement affichable, restreint a l'espece choisie et a ses varietes, puis prive des
     * arbres a verifier si le filtre le demande (#265).
     *
     * Ces deux restrictions se font ici et pas au serveur : l'information est deja dans le lot
     * recu, et le lot reste celui du perimetre interroge — comme le filtre d'espece, elles
     * retirent des marqueurs sans en recharger d'autres.
     */
    val visibleTrees: List<Tree>
        get() {
            val bySpecies =
                selectedSpeciesRollupId
                    ?.let { rollup -> nearbyTrees.filter { speciesRollup[it.species.id] == rollup } }
                    ?: nearbyTrees
            return if (filter.hideNeedsCheck) bySpecies.filterNot { it.needsCheck } else bySpecies
        }

    /**
     * Point depuis lequel toute distance affichee est mesuree (#260) : la position reelle si elle
     * est connue, sinon le centre interroge — jamais un repli par defaut, meme raison que
     * [fr.frux.feature.map.MapViewModel.frameVisibleResults]. Null tant qu'aucun des deux n'est
     * connu, et la surface dit alors « Distance inconnue » plutot qu'un chiffre invente.
     */
    val distanceReference: Location?
        get() = userLocation ?: queryCenter

    /**
     * [visibleTrees] trie par distance croissante pour [NearbyTreesSheet] (#196 lot 6), depuis
     * [distanceReference].
     */
    val nearbySorted: List<Tree>
        get() = visibleTrees.sortedByDistance(distanceReference)
}

/**
 * Tri par distance croissante ([distanceMetersFrom]). Les arbres sans distance calculable
 * finissent en fin de liste, dans leur ordre d'origine (tri stable) : mieux vaut les montrer en
 * dernier qu'en tete sous une distance inventee.
 */
internal fun List<Tree>.sortedByDistance(reference: Location?): List<Tree> {
    val (withDistance, without) = partition { it.distanceMetersFrom(reference) != null }
    return withDistance.sortedBy { it.distanceMetersFrom(reference) } + without
}
